import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
	StatusToastOverlayBackdrop,
	StatusToastOverlayContent,
	StatusToastOverlayCloseButton,
	StatusToastOverlayTitle,
	StatusToastOverlayMainText,
	StatusToastOverlayItem,
	StatusToastOverlayItemTitle,
	StatusToastOverlayItemList,
	StatusToastOverlayItemBullet,
} from './StatusToast.style';
import { OverlayItem } from './StatusToastContext';

interface StatusToastOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	mainText: string;
	items: OverlayItem[];
}

const ANIMATION_DURATION_MS = 300;

export const StatusToastOverlay = ({
	isOpen,
	onClose,
	title,
	mainText,
	items,
}: StatusToastOverlayProps) => {
	const overlayRef = useRef<HTMLDivElement>(null);
	const [isExiting, setIsExiting] = useState(false);
	const [shouldRender, setShouldRender] = useState(false);
	const wasOpenRef = useRef(false);
	const preservedContentRef = useRef<{
		title: string;
		mainText: string;
		items: OverlayItem[];
	} | null>(null);
	const [preservedContent, setPreservedContent] = useState<{
		title: string;
		mainText: string;
		items: OverlayItem[];
	} | null>(null);

	const handleClose = useCallback(() => {
		if (!isExiting && preservedContentRef.current) {
			setIsExiting(true);
			document.body.style.overflow = '';
			setTimeout(() => {
				setShouldRender(false);
				setPreservedContent(null);
				preservedContentRef.current = null;
				wasOpenRef.current = false;
				onClose();
			}, ANIMATION_DURATION_MS);
		}
	}, [isExiting, onClose]);

	// Handle opening the overlay - only when isOpen transitions from false to true
	useEffect(() => {
		if (isOpen && !wasOpenRef.current && !shouldRender) {
			// Opening for the first time (transition from closed to open)
			const content = { title, mainText, items };
			setShouldRender(true);
			setIsExiting(false);
			setPreservedContent(content);
			preservedContentRef.current = content;
			wasOpenRef.current = true;
			document.body.style.overflow = 'hidden';
		} else if (!isOpen && wasOpenRef.current) {
			// Track when overlay closes
			wasOpenRef.current = false;
		}
		// Don't update content if already rendered - preserve it during exit
	}, [isOpen, title, mainText, items, shouldRender]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen && !isExiting) {
				handleClose();
			}
		};

		if (isOpen && !isExiting) {
			document.addEventListener('keydown', handleEscape);
		}

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpen, isExiting, handleClose]);

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === overlayRef.current && !isExiting) {
			handleClose();
		}
	};

	if (!shouldRender || !preservedContent) {
		return null;
	}

	// Use preservedContent from state, but fallback to ref if state is cleared
	const content = preservedContent || preservedContentRef.current;
	if (!content) {
		return null;
	}

	return (
		<StatusToastOverlayBackdrop ref={overlayRef} onClick={handleBackdropClick} $isExiting={isExiting}>
			<StatusToastOverlayContent onClick={(e) => e.stopPropagation()} $isExiting={isExiting}>
				<StatusToastOverlayCloseButton onClick={handleClose} icon="x-slim" aria-label="Close" />
				<StatusToastOverlayTitle>{content.title}</StatusToastOverlayTitle>
				<StatusToastOverlayMainText>{content.mainText}</StatusToastOverlayMainText>
				{content.items.map((item, index) => (
					<StatusToastOverlayItem key={index}>
						<StatusToastOverlayItemTitle>{item.title}</StatusToastOverlayItemTitle>
						<StatusToastOverlayItemList>
							{item.bullets.map((bullet, bulletIndex) => (
								<StatusToastOverlayItemBullet key={bulletIndex}>
									{bullet}
								</StatusToastOverlayItemBullet>
							))}
						</StatusToastOverlayItemList>
					</StatusToastOverlayItem>
				))}
			</StatusToastOverlayContent>
		</StatusToastOverlayBackdrop>
	);
};

